import { useQuery } from '@tanstack/react-query';
import { supabase as _supabase } from '@/integrations/supabase/client';
import { useCurrentAppUser } from './useCurrentAppUser';
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const supabase = _supabase as any;

export type OutboundStatus = 'pending' | 'called_no_answer' | 'called_not_interested' | 'deal_created';

export interface OutboundContact {
  id: string; // assignment id
  contact_id: string;
  sdr_id: string;
  status: OutboundStatus;
  call_attempts: number;
  notes: string | null;
  assigned_at: string;
  updated_at: string;
  batch_label: string | null;
  contact: {
    id: string;
    first_name: string | null;
    last_name: string | null;
    primary_phone: string | null;
    primary_phone_country_code: string | null;
    primary_email: string | null;
    country_of_birth: string | null;
    language: string | null;
    do_not_call: boolean | null;
    do_not_message: boolean | null;
  };
  last_call_at: string | null;
}

export type BatchFilter = 'all' | 'normal' | string;

export function useOutboundQueue(batchFilter: BatchFilter = 'all') {
  const { data: currentUser } = useCurrentAppUser();
  const sdrId = currentUser?.id;

  return useQuery({
    queryKey: ['outbound-queue', sdrId, batchFilter],
    queryFn: async (): Promise<OutboundContact[]> => {
      if (!sdrId) return [];

      let query = supabase
        .from('outbound_daily_assignments')
        .select(`
          id,
          contact_id,
          sdr_id,
          status,
          call_attempts,
          notes,
          assigned_at,
          updated_at,
          batch_label,
          contact:contacts!outbound_daily_assignments_contact_id_fkey(
            id,
            first_name,
            last_name,
            primary_phone,
            primary_phone_country_code,
            primary_email,
            country_of_birth,
            language,
            do_not_call,
            do_not_message
          )
        `)
        .eq('sdr_id', sdrId)
        .not('status', 'in', '("deal_created","called_not_interested")');

      if (batchFilter === 'normal') {
        query = query.is('batch_label', null);
      } else if (batchFilter !== 'all') {
        query = query.eq('batch_label', batchFilter);
      }

      const { data, error } = await query
        .order('status', { ascending: false }) // 'pending' > 'called_no_answer' -> pendentes primeiro
        .order('assigned_at', { ascending: true })
        .limit(1000);

      if (error) throw error;


      // Enrich with last call date
      const contactIds = (data ?? []).map((d) => d.contact_id);
      let lastCallMap: Record<string, string> = {};

      if (contactIds.length > 0) {
        // Evita estourar o limite de URL do PostgREST ao carregar uma fila de
        // 1.000 contatos. Cada lote permanece pequeno e pode falhar de forma
        // isolada sem derrubar toda a fila.
        const chunks: string[][] = [];
        for (let index = 0; index < contactIds.length; index += 100) {
          chunks.push(contactIds.slice(index, index + 100));
        }
        const results = await Promise.all(
          chunks.map((chunk) =>
            supabase
              .from('v_contact_calls_with_handler')
              .select('contact_id, started_at')
              .in('contact_id', chunk)
              .order('started_at', { ascending: false }),
          ),
        );
        const calls = results.flatMap((result) => {
          if (result.error) {
            console.error('[OutboundQueue] Falha ao carregar histórico de chamadas:', result.error);
            return [];
          }
          return result.data ?? [];
        });

        // Keep only the most recent call per contact
        calls.forEach((c) => {
          if (c.contact_id && !lastCallMap[c.contact_id]) {
            lastCallMap[c.contact_id] = c.started_at ?? '';
          }
        });
      }

      // Mesma janela usada por fn_get_outbound_eligible_contacts no banco.
      const RECONTACT_WINDOW_DAYS = 5;
      const RECONTACT_WINDOW_MS = RECONTACT_WINDOW_DAYS * 24 * 60 * 60 * 1000;

      return (data ?? [])
        .map((d) => ({
          ...d,
          contact: Array.isArray(d.contact) ? d.contact[0] : d.contact,
          last_call_at: lastCallMap[d.contact_id] ?? null,
        }))
        .filter((item) => {
          // Manual imports (with batch_label) bypass the safety net —
          // the manager explicitly assigned this list, show everything.
          if (item.batch_label) return true;

          // Safety net only for the normal queue (daily cron):
          // hide contacts that were incorrectly assigned when a call existed
          // within the current recontact window *before* the assignment was created.
          if (!item.last_call_at) return true;
          const callDate = new Date(item.last_call_at).getTime();
          const assignedDate = new Date(item.assigned_at).getTime();
          const ageMs = Date.now() - callDate;
          // Only filter if the call pre-dates the assignment (it was a pre-existing call,
          // not a call the SDR made from the outbound queue).
          return !(ageMs < RECONTACT_WINDOW_MS && callDate < assignedDate);
        }) as OutboundContact[];
    },
    enabled: !!sdrId,
    staleTime: 2 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

export function useOutboundStats(queue: OutboundContact[]) {
  const pending = queue.filter((c) => c.status === 'pending').length;
  const noAnswer = queue.filter((c) => c.status === 'called_no_answer').length;
  const notInterested = queue.filter((c) => c.status === 'called_not_interested').length;
  const total = queue.length;

  return { pending, noAnswer, notInterested, total };
}

export function useOutboundStatsFromDB(batchFilter: BatchFilter = 'all') {
  const { data: currentUser } = useCurrentAppUser();
  const sdrId = currentUser?.id;

  return useQuery({
    queryKey: ['outbound-stats-db', sdrId, batchFilter],
    queryFn: async () => {
      if (!sdrId) return { pending: 0, noAnswer: 0, notInterested: 0, total: 0 };

      const countByStatus = async (status: OutboundStatus) => {
        let q = supabase
          .from('outbound_daily_assignments')
          .select('*', { count: 'exact', head: true })
          .eq('sdr_id', sdrId)
          .eq('status', status);

        if (batchFilter === 'normal') {
          q = q.is('batch_label', null);
        } else if (batchFilter !== 'all') {
          q = q.eq('batch_label', batchFilter);
        }

        const { count, error } = await q;
        if (error) throw error;
        return count ?? 0;
      };

      const [pending, noAnswer, notInterested] = await Promise.all([
        countByStatus('pending'),
        countByStatus('called_no_answer'),
        countByStatus('called_not_interested'),
      ]);

      const total = pending; // somente contatos ainda ativos para discagem

      return { pending, noAnswer, notInterested, total };
    },

    enabled: !!sdrId,
    staleTime: 30_000,
  });
}

/**
 * Returns the distinct batch_label values present in the SDR's queue
 * (excluding closed statuses), so the UI can build a "Lote" dropdown.
 * Includes a flag indicating whether the SDR has any unbatched ("normal") rows.
 */
export function useOutboundBatches() {
  const { data: currentUser } = useCurrentAppUser();
  const sdrId = currentUser?.id;

  return useQuery({
    queryKey: ['outbound-batches', sdrId],
    queryFn: async (): Promise<{ batches: string[]; hasNormal: boolean }> => {
      if (!sdrId) return { batches: [], hasNormal: false };

      const { data, error } = await supabase
        .from('outbound_daily_assignments')
        .select('batch_label')
        .eq('sdr_id', sdrId)
        .not('status', 'in', '("deal_created","called_not_interested")');

      if (error) throw error;

      const rows = (data ?? []) as Array<{ batch_label: string | null }>;
      const set = new Set<string>();
      let hasNormal = false;
      rows.forEach((r) => {
        if (r.batch_label) set.add(r.batch_label);
        else hasNormal = true;
      });
      return { batches: Array.from(set).sort(), hasNormal };
    },
    enabled: !!sdrId,
    staleTime: 60_000,
  });
}
